import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import { Role, Skill } from '../../types/auth';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';

const CreateUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    name: '',
    password: '',
    roleId: '',
    skillIds: [] as string[],
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchSkills();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get('/roles');
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await apiClient.get('/users/skills/list');
      setSkills(response.data.data);
    } catch (err) {
      console.error('Failed to fetch skills:', err);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        username: formData.username.trim() || undefined,
      };
      await apiClient.post('/users', payload);
      navigate('/admin/users', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkillToggle = (skillId: string) => {
    setFormData((prev) => ({
      ...prev,
      skillIds: prev.skillIds.includes(skillId)
        ? prev.skillIds.filter((id) => id !== skillId)
        : [...prev.skillIds, skillId],
    }));
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Create New User</h1>
        <p className="text-slate-500 mt-1">Add a new member to the system</p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
        <Input
          label="Name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="Full name"
        />

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          placeholder="email@company.com"
        />

        <Input
          label="Username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          placeholder="Unique staff username"
          hint="Used for login and staff identification. Leave blank to auto-generate."
        />

        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required
          placeholder="Min. 8 characters"
          hint="Must include uppercase, lowercase, number, and special character"
        />

        <Select
          label="Role"
          value={formData.roleId}
          onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
          required
        >
          <option value="">Select a role</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </Select>

        <div>
          <p className="block text-sm font-semibold text-slate-700 mb-2">Skills</p>
          <div className="border border-slate-200 rounded-lg p-3 space-y-1 max-h-52 overflow-y-auto">
            {skills.map((skill) => (
              <label
                key={skill.id}
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.skillIds.includes(skill.id)}
                  onChange={() => handleSkillToggle(skill.id)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-800 font-medium">{skill.name}</span>
                {skill.description && (
                  <span className="text-xs text-slate-400">{skill.description}</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" loading={isLoading} className="flex-1">
            {isLoading ? 'Creating...' : 'Create User'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/users')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateUser;
